import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Paper, Switch, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Button, TextField, Chip
} from "@mui/material";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { fetchData, putData } from "../../BaseUrl";
import Toast from "../../DynamicComponent/Toast";

const FORM_LABELS = {
  enrollment: "Student Enrollment",
  withdrawal: "Withdrawal Enquiry",
  leave: "Leave Management",
  complaint: "Complaint Management",
  pickup_drop: "Pickup & Drop",
  biliteracy: "Seal of Biliteracy",
};

const AdminFormAccess = () => {
  const [configs, setConfigs] = useState([]);
  const [edited, setEdited] = useState({});
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });

  const load = useCallback(async () => {
    try {
      const res = await fetchData("admin/form-access/list");
      if (res?.data) {
        const arr = Array.isArray(res.data) ? res.data : [];
        setConfigs(arr);
        const init = {};
        arr.forEach(c => { init[c.formType] = { startDate: c.startDate || "", endDate: c.endDate || "", isActive: c.isActive }; });
        setEdited(init);
      }
    } catch (_) {
      // Use defaults if API not available yet
      const defaults = Object.keys(FORM_LABELS).map(ft => ({
        formType: ft, label: FORM_LABELS[ft], startDate: null, endDate: null, isActive: true
      }));
      setConfigs(defaults);
      const init = {};
      defaults.forEach(c => { init[c.formType] = { startDate: "", endDate: "", isActive: true }; });
      setEdited(init);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleChange = (formType, field, value) => {
    setEdited(prev => ({ ...prev, [formType]: { ...prev[formType], [field]: value } }));
  };

  const handleSave = async (formType) => {
    try {
      const data = edited[formType];
      await putData(`admin/form-access/update/${formType}`, data);
      setToast({ open: true, severity: "success", message: `${FORM_LABELS[formType] || formType} settings saved!` });
      load();
    } catch (err) {
      setToast({ open: true, severity: "error", message: "Failed to save. Please try again." });
    }
  };

  const isActive = (formType) => {
    const cfg = edited[formType];
    if (!cfg) return false;
    if (!cfg.isActive) return false;
    const today = new Date().toISOString().split("T")[0];
    if (cfg.startDate && today < cfg.startDate) return false;
    if (cfg.endDate && today > cfg.endDate) return false;
    return true;
  };

  return (
    <div style={{ textAlign: "left", padding: "1rem" }}>
      <Toast open={toast.open} handleClose={() => setToast({ ...toast, open: false })}
        severity={toast.severity} message={toast.message} />

      <p style={{ color: "rgba(0,0,0,0.45)", fontWeight: "bold", fontSize: "14px", margin: "0 0 12px" }}>
        Pages / Form Access Duration
      </p>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
        <AccessTimeIcon sx={{ color: "#244C77", fontSize: "28px" }} />
        <Box>
          <Typography sx={{ fontWeight: "bold", fontSize: "22px", color: "#111827" }}>
            Form Access Duration
          </Typography>
          <Typography sx={{ fontSize: "13px", color: "#6B7280" }}>
            Control when each form is open for submissions
          </Typography>
        </Box>
      </Box>

      <Paper elevation={0} sx={{ borderRadius: "16px", boxShadow: "0 2px 12px rgba(0,0,0,0.07)", overflow: "hidden" }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ backgroundColor: "#1E293B" }}>
              <TableRow>
                {["Form", "Status", "Start Date", "End Date", "Current State", "Action"].map(h => (
                  <TableCell key={h} sx={{ color: "#fff", fontWeight: 600, fontSize: "13px" }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {configs.map((cfg, idx) => {
                const ed = edited[cfg.formType] || {};
                const active = isActive(cfg.formType);
                return (
                  <TableRow key={cfg.formType} sx={{ "&:hover": { backgroundColor: "#f9fafb" }, backgroundColor: idx % 2 === 0 ? "#fff" : "#FAFAFA" }}>
                    <TableCell>
                      <Typography sx={{ fontWeight: 600, fontSize: "13px", color: "#111827" }}>
                        {FORM_LABELS[cfg.formType] || cfg.formType}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={ed.isActive ?? true}
                        onChange={(e) => handleChange(cfg.formType, "isActive", e.target.checked)}
                        sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: "#4caf50" }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#4caf50" } }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="date" size="small" value={ed.startDate || ""}
                        onChange={(e) => handleChange(cfg.formType, "startDate", e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: "13px" }, minWidth: 140 }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="date" size="small" value={ed.endDate || ""}
                        onChange={(e) => handleChange(cfg.formType, "endDate", e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: "13px" }, minWidth: 140 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={active ? "Open" : "Closed"}
                        size="small"
                        sx={{
                          backgroundColor: active ? "#e8f5e9" : "#fef2f2",
                          color: active ? "#2e7d32" : "#c62828",
                          fontWeight: 600, fontSize: "12px"
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        startIcon={<SaveOutlinedIcon />}
                        onClick={() => handleSave(cfg.formType)}
                        sx={{
                          background: "linear-gradient(90deg, #4A7BA8, #244C77)",
                          color: "#fff", fontSize: "12px", textTransform: "none",
                          borderRadius: "8px", px: 2,
                        }}
                      >
                        Save
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </div>
  );
};

export default AdminFormAccess;
